// Periodo de reporte de la banda «Todos los números filtrados a:» — lógica pura, sin React.
import dayjs from 'dayjs';
import { mxDay, mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';

/** Van con sufijo `d` para que el router no los lea como número en `?periodo=`. */
export type ReportPeriod = '7d' | '30d' | '90d' | 'all';

export const DEFAULT_REPORT_PERIOD: ReportPeriod = '30d';

export const REPORT_PERIOD_OPTIONS: { value: ReportPeriod; label: string }[] = [
    { value: '7d', label: 'Últimos 7 días' },
    { value: '30d', label: 'Últimos 30 días' },
    { value: '90d', label: 'Últimos 90 días' },
    { value: 'all', label: 'Todo el historial' },
];

/** Primer día que cubre «Todo el historial»: el endpoint exige un `from`. */
export const HISTORY_START_DAY = '2025-01-01';

/** Días que dura un mes para escalar la meta mensual, como el mock (`meta * d / 30`). */
const GOAL_MONTH_DAYS = 30;

/** Lee el `?periodo=` de la URL; un valor desconocido cae al default. */
export function parseReportPeriod(value: string | undefined): ReportPeriod {
    return REPORT_PERIOD_OPTIONS.some((o) => o.value === value)
        ? (value as ReportPeriod)
        : DEFAULT_REPORT_PERIOD;
}

/** Días del periodo, o `null` en «Todo el historial». */
export function reportPeriodDays(period: ReportPeriod): number | null {
    return period === 'all' ? null : Number.parseInt(period, 10);
}

export function reportPeriodLabel(period: ReportPeriod): string {
    return REPORT_PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? '';
}

/** Días de calendario en CDMX (`YYYY-MM-DD`) que cubre el periodo, hoy incluido. */
export function reportPeriodRange(
    period: ReportPeriod,
    now: Date = new Date(),
): { fromDay: string; toDay: string } {
    const toDay = mxDay(now.toISOString());
    const days = reportPeriodDays(period);
    const fromDay =
        days === null
            ? HISTORY_START_DAY
            : dayjs(toDay)
                  .subtract(days - 1, 'day')
                  .format('YYYY-MM-DD');
    return { fromDay, toDay };
}

/** La ventana `from`/`to` (ISO UTC) que se manda a las KPIs. */
export function reportPeriodWindow(
    period: ReportPeriod,
    now: Date = new Date(),
): { from: string; to: string } {
    const { fromDay, toDay } = reportPeriodRange(period, now);
    return { from: mxDayStartUtc(fromDay), to: mxDayEndUtc(toDay) };
}

/** Meta mensual escalada al periodo; en «Todo el historial» no hay meta comparable. */
export function scaleMonthlyGoal(monthCents: number, period: ReportPeriod): number | null {
    const days = reportPeriodDays(period);
    if (days === null) return null;
    return Math.round((monthCents * days) / GOAL_MONTH_DAYS);
}
